/**
 * Speech Synthesis Utility
 *
 * Uses the Web Speech API to generate audio for Chinese text
 * Provides high-quality Chinese pronunciation without audio files
 */

/**
 * Speak Chinese text using browser's text-to-speech
 *
 * @param {string} text - Chinese text to speak (hanzi or pinyin)
 * @param {Object} options - Speech options
 * @param {number} options.rate - Speech rate (0.1 to 10, default 0.9)
 * @param {number} options.pitch - Voice pitch (0 to 2, default 1)
 * @param {number} options.volume - Volume (0 to 1, default 1)
 * @returns {Promise} - Resolves when speech completes
 */
export function speakChinese(text, options = {}) {
  return new Promise((resolve, reject) => {
    // Check if speech synthesis is supported
    if (!window.speechSynthesis) {
      console.warn('Speech synthesis not supported in this browser');
      reject(new Error('Speech synthesis not supported'));
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);

    // Set language to Chinese
    utterance.lang = 'zh-CN'; // Simplified Chinese

    // Apply options
    utterance.rate = options.rate ?? 0.9; // Slightly slower for clarity
    utterance.pitch = options.pitch ?? 1.0;
    utterance.volume = options.volume ?? 1.0;

    // Try to find a Chinese voice
    const voices = window.speechSynthesis.getVoices();
    const chineseVoice = voices.find(voice =>
      voice.lang.startsWith('zh') ||
      voice.lang.includes('Chinese')
    );

    if (chineseVoice) {
      utterance.voice = chineseVoice;
    }

    // Event handlers
    utterance.onend = () => resolve();
    utterance.onerror = (error) => {
      console.error('Speech synthesis error:', error);
      reject(error);
    };

    // Speak
    window.speechSynthesis.speak(utterance);
  });
}

/**
 * Get available Chinese voices
 *
 * @returns {Array} - Array of available Chinese voices
 */
export function getChineseVoices() {
  if (!window.speechSynthesis) return [];

  const voices = window.speechSynthesis.getVoices();
  return voices.filter(voice =>
    voice.lang.startsWith('zh') ||
    voice.lang.toLowerCase().includes('chinese')
  );
}

/**
 * Load voices (needed for some browsers)
 * Call this on app initialization
 */
export function loadVoices() {
  return new Promise((resolve) => {
    // Voices may load asynchronously
    if (window.speechSynthesis.getVoices().length > 0) {
      resolve(window.speechSynthesis.getVoices());
    } else {
      window.speechSynthesis.onvoiceschanged = () => {
        resolve(window.speechSynthesis.getVoices());
      };
    }
  });
}

/**
 * Stop any ongoing speech
 */
export function stopSpeech() {
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}
